import { createSlice } from '@reduxjs/toolkit';
import settingsService from '../services/settingsService';
import { Notifications } from './models/notifications.model';
import * as uiSlice from './ui.slice';
import { DateTime } from 'luxon';
import { PackageSettings } from './models/package-setting.model';
import {
  ApplicationGlobalSettingRequest,
  ApplicationGlobalSettingResponse,
  CreateApplicationGlobalSettingRequest,
  UpdateApplicationGlobalSettingRequest,
} from './models/application-setting.model';
import { SortModel } from './models/sort-model.model';

interface SettingsState {
  // Notifications
  loadingNotifications: boolean;
  loadedNotifications: boolean;
  savingNotifications: boolean;
  savedNotifications: boolean;
  notifications: Notifications;

  // Package
  loadingPackageSettings: boolean;
  loadedPackageSettings: boolean;
  savingPackageSettings: boolean;
  savedPackageSettings: boolean;
  packageSettings: PackageSettings;

  // Global Settings
  loadingGlobalSettings: boolean;
  loadedGlobalSettings: boolean;
  globalSettings: ApplicationGlobalSettingResponse[];

  loadingSingleGlobalSetting: boolean;
  loadedSingleGlobalSetting: boolean;
  singleGlobalSetting: ApplicationGlobalSettingResponse | null;

  deletingApplicationGlobalSetting: boolean;
  deletedApplicationGlobalSetting: boolean;

  savingApplicationGlobalSetting: boolean;
  savedApplicationGlobalSetting: boolean;
}

export const initialState: SettingsState = {
  loadingNotifications: false,
  loadedNotifications: false,
  savingNotifications: false,
  savedNotifications: false,
  notifications: {
    oneDayAheadTripNotification: 0,
    dayOfTripNotification: 0,
    willCallDropoffNotification: 0,
    followUpWillCallMessage: 0,
    tripLegPackagingTime: DateTime.now().toISO(),
    tripLegPackagingDaysPrior: 0,
    tripLegPackageAcceptanceTimeout: 0,
    leadTimeForSmsToDriver: 0,
  },

  loadingPackageSettings: false,
  loadedPackageSettings: false,
  savingPackageSettings: false,
  savedPackageSettings: false,
  packageSettings: {
    deliveryTimingToProviderStartTime: DateTime.now().toISO(),
    deliveryTimingToProviderEndTime: DateTime.now().toISO(),
    deliveryTimingToProviderDaysPrior: 0,
  },

  loadingGlobalSettings: false,
  loadedGlobalSettings: false,
  globalSettings: [],

  loadingSingleGlobalSetting: false,
  loadedSingleGlobalSetting: false,
  singleGlobalSetting: null,

  deletingApplicationGlobalSetting: false,
  deletedApplicationGlobalSetting: false,

  savingApplicationGlobalSetting: false,
  savedApplicationGlobalSetting: false,
};

export const slice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    // Notifications
    setNotifications: (
      state: SettingsState,
      action: { payload: Notifications },
    ) => {
      state.notifications = action.payload;
    },
    setLoadingNotifications: (
      state: SettingsState,
      action: { payload: boolean },
    ) => {
      state.loadingNotifications = action.payload;
    },
    setLoadedNotifications: (
      state: SettingsState,
      action: { payload: boolean },
    ) => {
      state.loadedNotifications = action.payload;
    },
    setSavingNotifications: (
      state: SettingsState,
      action: { payload: boolean },
    ) => {
      state.savingNotifications = action.payload;
    },
    setSavedNotifications: (
      state: SettingsState,
      action: { payload: boolean },
    ) => {
      state.savedNotifications = action.payload;
    },
    // Package
    setPackageSettings: (
      state: SettingsState,
      action: { payload: PackageSettings },
    ) => {
      state.packageSettings = action.payload;
    },
    setLoadingPackageSettings: (
      state: SettingsState,
      action: { payload: boolean },
    ) => {
      state.loadingPackageSettings = action.payload;
    },
    setLoadedPackageSettings: (
      state: SettingsState,
      action: { payload: boolean },
    ) => {
      state.loadedPackageSettings = action.payload;
    },
    setSavingPackageSettings: (
      state: SettingsState,
      action: { payload: boolean },
    ) => {
      state.savingPackageSettings = action.payload;
    },
    setSavedPackageSettings: (
      state: SettingsState,
      action: { payload: boolean },
    ) => {
      state.savedPackageSettings = action.payload;
    },
    // Global Settings
    setGlobalSettings: (
      state: SettingsState,
      action: { payload: ApplicationGlobalSettingResponse[] },
    ) => {
      state.globalSettings = action.payload;
    },
    setLoadingGlobalSettings: (
      state: SettingsState,
      action: { payload: boolean },
    ) => {
      state.loadingGlobalSettings = action.payload;
    },
    setLoadedGlobalSettings: (
      state: SettingsState,
      action: { payload: boolean },
    ) => {
      state.loadedGlobalSettings = action.payload;
    },
    setSingleGlobalSetting: (
      state: SettingsState,
      action: { payload: ApplicationGlobalSettingResponse[] },
    ) => {
      state.singleGlobalSetting =
        action.payload.length > 0 ? action.payload[0] : null;
    },
    setLoadingSingleGlobalSetting: (
      state: SettingsState,
      action: { payload: boolean },
    ) => {
      state.loadingSingleGlobalSetting = action.payload;
    },
    setLoadedSingleGlobalSetting: (
      state: SettingsState,
      action: { payload: boolean },
    ) => {
      state.loadedSingleGlobalSetting = action.payload;
    },
    setDeletingApplicationGlobalSetting: (
      state: SettingsState,
      action: { payload: boolean },
    ) => {
      state.deletingApplicationGlobalSetting = action.payload;
    },
    setDeletedApplicationGlobalSetting: (
      state: SettingsState,
      action: { payload: boolean },
    ) => {
      state.deletedApplicationGlobalSetting = action.payload;
    },
    setSavingApplicationGlobalSetting: (
      state: SettingsState,
      action: { payload: boolean },
    ) => {
      state.savingApplicationGlobalSetting = action.payload;
    },
    setSavedApplicationGlobalSetting: (
      state: SettingsState,
      action: { payload: boolean },
    ) => {
      state.savedApplicationGlobalSetting = action.payload;
    },
    deleteApplicationGlobalSetting: (
      state: SettingsState,
      action: { payload: number },
    ) => {
      state.globalSettings = state.globalSettings.filter(
        (setting) => setting.applicationGlobalSettingID !== action.payload,
      );
    },
    upsertApplicationGlobalSetting: (
      state: SettingsState,
      action: { payload: ApplicationGlobalSettingResponse },
    ) => {
      const globalSettings = [...state.globalSettings];
      const idx = state.globalSettings.findIndex(
        (setting) =>
          setting.applicationGlobalSettingID ===
          action.payload.applicationGlobalSettingID,
      );
      if (idx >= 0) {
        globalSettings.splice(idx, 1, action.payload);
        state.globalSettings = globalSettings;
      } else {
        state.globalSettings = [action.payload, ...globalSettings];
      }
    },
  },
});

export const { setNotifications } = slice.actions;
export const { setLoadingNotifications } = slice.actions;
export const { setLoadedNotifications } = slice.actions;
export const { setSavingNotifications } = slice.actions;
export const { setSavedNotifications } = slice.actions;
export const { setPackageSettings } = slice.actions;
export const { setLoadingPackageSettings } = slice.actions;
export const { setLoadedPackageSettings } = slice.actions;
export const { setSavingPackageSettings } = slice.actions;
export const { setSavedPackageSettings } = slice.actions;
export const { setGlobalSettings } = slice.actions;
export const { setLoadingGlobalSettings } = slice.actions;
export const { setLoadedGlobalSettings } = slice.actions;
export const { setSingleGlobalSetting } = slice.actions;
export const { setLoadingSingleGlobalSetting } = slice.actions;
export const { setLoadedSingleGlobalSetting } = slice.actions;
export const { setDeletingApplicationGlobalSetting } = slice.actions;
export const { setDeletedApplicationGlobalSetting } = slice.actions;
export const { setSavingApplicationGlobalSetting } = slice.actions;
export const { setSavedApplicationGlobalSetting } = slice.actions;
export const { deleteApplicationGlobalSetting } = slice.actions;
export const { upsertApplicationGlobalSetting } = slice.actions;

export const fetchNotificationSettingsAsync = () => (dispatch: Function) => {
  dispatch(setLoadingNotifications(true));
  dispatch(setLoadedNotifications(false));
  settingsService.getNotificationSettings().subscribe({
    next: (notifications) => {
      dispatch(setNotifications(notifications));
      dispatch(setLoadingNotifications(false));
      dispatch(setLoadedNotifications(true));
    },
    error: (err) => {
      dispatch(uiSlice.setErrorMessage(err?.message));
      dispatch(setLoadingNotifications(false));
      dispatch(setLoadedNotifications(false));
    },
  });
};

export const updateNotificationSettingsAsync =
  (notifications: Notifications) => (dispatch: Function) => {
    dispatch(setSavingNotifications(true));
    dispatch(setSavedNotifications(false));
    settingsService.updateNotificationSettings(notifications).subscribe({
      next: (newItem) => {
        dispatch(setNotifications(newItem));
        dispatch(
          uiSlice.setSuccessMessage('snackbar.saveNotificationsSuccess'),
        );
        dispatch(setSavingNotifications(false));
        dispatch(setSavedNotifications(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingNotifications(false));
        dispatch(setSavedNotifications(false));
      },
    });
  };

export const fetchPackageSettingsAsync = () => (dispatch: Function) => {
  dispatch(setLoadingPackageSettings(true));
  dispatch(setLoadedPackageSettings(false));
  settingsService.getPackageSettings().subscribe({
    next: (packageSettings) => {
      dispatch(setPackageSettings(packageSettings));
      dispatch(setLoadingPackageSettings(false));
      dispatch(setLoadedPackageSettings(true));
    },
    error: (err) => {
      dispatch(uiSlice.setErrorMessage(err?.message));
      dispatch(setLoadingPackageSettings(false));
      dispatch(setLoadedPackageSettings(false));
    },
  });
};

export const updatePackageSettingsAsync =
  (packageSettings: PackageSettings) => (dispatch: Function) => {
    dispatch(setSavingPackageSettings(true));
    dispatch(setSavedPackageSettings(false));
    settingsService.updatePackageSettings(packageSettings).subscribe({
      next: (newItem) => {
        dispatch(setPackageSettings(newItem));
        dispatch(
          uiSlice.setSuccessMessage('snackbar.savePackageSettingsSuccess'),
        );
        dispatch(setSavingPackageSettings(false));
        dispatch(setSavedPackageSettings(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingPackageSettings(false));
        dispatch(setSavedPackageSettings(false));
      },
    });
  };

export const fetchSingleApplicationGlobalSettingAsync =
  ({
    applicationTitleBC,
    applicationSettingBC,
    search,
    sortModel,
  }: ApplicationGlobalSettingRequest) =>
  (dispatch: Function) => {
    dispatch(setLoadingGlobalSettings(true));
    dispatch(setLoadedGlobalSettings(false));
    settingsService
      .getApplicationGlobalSetting({
        applicationTitleBC,
        applicationSettingBC,
        search,
        sortModel,
      })
      .subscribe({
        next: (globalSettings: ApplicationGlobalSettingResponse[]) => {
          dispatch(setSingleGlobalSetting(globalSettings));
          dispatch(setLoadingSingleGlobalSetting(false));
          dispatch(setLoadedSingleGlobalSetting(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setLoadingSingleGlobalSetting(false));
          dispatch(setLoadedSingleGlobalSetting(false));
        },
      });
  };

export const fetchAllApplicationGlobalSettingsAsync =
  (search: string = '', sortModel: SortModel | null = null) =>
  (dispatch: Function) => {
    dispatch(setLoadingGlobalSettings(true));
    dispatch(setLoadedGlobalSettings(false));
    settingsService
      .getApplicationGlobalSettings({
        applicationTitleBC: undefined,
        applicationSettingBC: undefined,
        search,
        sortModel,
      })
      .subscribe({
        next: (globalSettings: ApplicationGlobalSettingResponse[]) => {
          dispatch(setGlobalSettings(globalSettings));
          dispatch(setLoadingGlobalSettings(false));
          dispatch(setLoadedGlobalSettings(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setLoadingGlobalSettings(false));
          dispatch(setLoadedGlobalSettings(false));
        },
      });
  };

export const deleteApplicationGlobalSettingAsync =
  (applicationGlobalSettingID: number) => (dispatch: Function) => {
    dispatch(setDeletingApplicationGlobalSetting(true));
    dispatch(setDeletedApplicationGlobalSetting(false));
    settingsService
      .deleteApplicationGlobalSetting(applicationGlobalSettingID)
      .subscribe({
        next: () => {
          dispatch(setDeletedApplicationGlobalSetting(true));
          dispatch(setDeletingApplicationGlobalSetting(false));
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.deleteApplicationGlobalSettingSuccess',
            ),
          );
          dispatch(deleteApplicationGlobalSetting(applicationGlobalSettingID));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setDeletingApplicationGlobalSetting(false));
          dispatch(setDeletedApplicationGlobalSetting(false));
        },
      });
  };

export const createApplicationGlobalSettingAsync =
  (payload: CreateApplicationGlobalSettingRequest) => (dispatch: Function) => {
    dispatch(setSavingApplicationGlobalSetting(true));
    dispatch(setSavedApplicationGlobalSetting(false));
    settingsService.createApplicationGlobalSetting(payload).subscribe({
      next: (newItem) => {
        dispatch(setSavingApplicationGlobalSetting(false));
        dispatch(setSavedApplicationGlobalSetting(true));
        dispatch(
          uiSlice.setSuccessMessage(
            'snackbar.createApplicationGlobalSettingSuccess',
          ),
        );
        dispatch(upsertApplicationGlobalSetting(newItem));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingApplicationGlobalSetting(false));
        dispatch(setSavedApplicationGlobalSetting(false));
      },
    });
  };

export const updateApplicationGlobalSettingAsync =
  (payload: UpdateApplicationGlobalSettingRequest) => (dispatch: Function) => {
    dispatch(setSavingApplicationGlobalSetting(true));
    dispatch(setSavedApplicationGlobalSetting(false));
    settingsService.updateApplicationGlobalSetting(payload).subscribe({
      next: (newItem) => {
        dispatch(setSavingApplicationGlobalSetting(false));
        dispatch(setSavedApplicationGlobalSetting(true));
        dispatch(
          uiSlice.setSuccessMessage(
            'snackbar.updateApplicationGlobalSettingSuccess',
          ),
        );
        dispatch(upsertApplicationGlobalSetting(newItem));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingApplicationGlobalSetting(false));
        dispatch(setSavedApplicationGlobalSetting(false));
      },
    });
  };

export const selectNotificationSettings = (state: {
  settings: SettingsState;
}) => state.settings.notifications;
export const selectLoadingNotifications = (state: {
  settings: SettingsState;
}) => state.settings.loadingNotifications;
export const selectLoadedNotifications = (state: { settings: SettingsState }) =>
  state.settings.loadedNotifications;
export const selectSavingNotifications = (state: { settings: SettingsState }) =>
  state.settings.savingNotifications;
export const selectSavedNotifications = (state: { settings: SettingsState }) =>
  state.settings.savedNotifications;
export const selectPackageSettings = (state: { settings: SettingsState }) =>
  state.settings.packageSettings;
export const selectLoadingPackageSettings = (state: {
  settings: SettingsState;
}) => state.settings.loadingPackageSettings;
export const selectLoadedPackageSettings = (state: {
  settings: SettingsState;
}) => state.settings.loadedPackageSettings;
export const selectSavingPackageSettings = (state: {
  settings: SettingsState;
}) => state.settings.savingPackageSettings;
export const selectSavedPackageSettings = (state: {
  settings: SettingsState;
}) => state.settings.savedPackageSettings;
export const selectGlobalSettings = (state: { settings: SettingsState }) =>
  state.settings.globalSettings;
export const selectLoadingGlobalSettings = (state: {
  settings: SettingsState;
}) => state.settings.loadingGlobalSettings;
export const selectLoadedGlobalSettings = (state: {
  settings: SettingsState;
}) => state.settings.loadedGlobalSettings;
export const selectSingleGlobalSetting = (state: { settings: SettingsState }) =>
  state.settings.singleGlobalSetting;
export const selectLoadingSingleGlobalSetting = (state: {
  settings: SettingsState;
}) => state.settings.loadingSingleGlobalSetting;
export const selectLoadedSingleGlobalSetting = (state: {
  settings: SettingsState;
}) => state.settings.loadedSingleGlobalSetting;
export const selectDeletingApplicationGlobalSetting = (state: {
  settings: SettingsState;
}) => state.settings.deletingApplicationGlobalSetting;
export const selectDeletedApplicationGlobalSetting = (state: {
  settings: SettingsState;
}) => state.settings.deletedApplicationGlobalSetting;
export const selectSavingApplicationGlobalSetting = (state: {
  settings: SettingsState;
}) => state.settings.savingApplicationGlobalSetting;
export const selectSavedApplicationGlobalSetting = (state: {
  settings: SettingsState;
}) => state.settings.savedApplicationGlobalSetting;

export default slice.reducer;
